import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';

const fmt = (n) => parseFloat(n || 0).toFixed(2);

const fmtFecha = (d) => {
  if (!d) return '';
  const s = d instanceof Date ? d.toISOString().slice(0, 10) : String(d).slice(0, 10);
  const [y, m, dia] = s.split('-');
  return `${dia}/${m}/${y}`;
};

const fmtHora = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const h = dt.getHours();
  const min = String(dt.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return `${String(h12).padStart(2, '0')}:${min} ${ampm}`;
};

/* ── CSS de impresión ── */
const CSS_PRINT = `
  @media print {
    @page { size: A4; margin: 0; }
    body  { margin: 0; padding: 12mm 18mm; background: #fff; }
  }
  body { font-family: 'Segoe UI', Arial, sans-serif; background: #fff; }
`;

export default function CajaCuadreImprimirPage() {
  const { id } = useParams();
  const [data,    setData]    = useState(null);
  const [empresa, setEmpresa] = useState(null);
  const [listo,   setListo]   = useState(false);
  const [error,   setError]   = useState('');

  /* Mismo patrón que VentaImprimirPage */
  useEffect(() => {
    Promise.all([
      api.get(`/api/cajas/sesiones/${id}/summary`),
      api.get('/api/empresa'),
    ])
      .then(([r1, re]) => {
        setData(r1.data.data);
        setEmpresa(re.data?.empresa || re.data?.data || re.data || {});
        setListo(true);
      })
      .catch(() => setError('No se pudo cargar el cuadre de caja.'));
  }, [id]);

  useEffect(() => {
    if (!listo) return;
    const t = setTimeout(() => window.print(), 700);
    return () => clearTimeout(t);
  }, [listo]);

  if (error) return (
    <><style>{CSS_PRINT}</style>
    <div style={{ padding: 40, color: '#dc2626', fontFamily: 'sans-serif' }}>Error: {error}</div></>
  );
  if (!listo) return (
    <><style>{CSS_PRINT}</style>
    <div style={{ padding: 40, color: '#64748b', fontFamily: 'sans-serif' }}>Cargando cuadre...</div></>
  );

  const { sesion, movimientos, monto_apertura, ventas_efectivo, ingresos,
          ingresos_digitales, egresos, total_efectivo, ventas_por_forma_pago, ventas } = data;

  const diferencia   = sesion.diferencia != null ? parseFloat(sesion.diferencia) : null;
  const totalContado = parseFloat(ventas?.total_contado || 0);
  const totalCredito = parseFloat(ventas?.total_credito || 0);
  const totalVentas  = parseFloat(ventas?.total_ventas  || 0);
  const estaAbierta  = sesion.estado === 'ABIERTA';

  /* ── Estilos de secciones (JS) ── */
  const s = {
    page:   { fontSize: 12, color: '#1e293b', maxWidth: 720, margin: '0 auto', padding: '20px 30px' },
    sec:    { marginTop: 14 },
    sTitle: { background: '#1e293b', color: '#e2e8f0', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, padding: '5px 10px' },
    row:    { display: 'flex', justifyContent: 'space-between', padding: '5px 10px', borderBottom: '1px solid #f1f5f9', alignItems: 'center' },
    rowSub: { display: 'flex', justifyContent: 'space-between', padding: '4px 20px', borderBottom: '1px solid #f8fafc', fontSize: 11.5, color: '#475569' },
    rowTot: { display: 'flex', justifyContent: 'space-between', padding: '7px 10px', background: '#f1f5f9', fontWeight: 700 },
    rowFin: { display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: '#1e293b', color: '#fff', fontWeight: 800, fontSize: 14 },
    lbl:    { color: '#475569' },
    val:    { fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
    tbl:    { width: '100%', borderCollapse: 'collapse', fontSize: 11 },
    th:     { background: '#f1f5f9', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', fontSize: 10, padding: '5px 8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' },
    td:     { padding: '4px 8px', borderBottom: '1px solid #f8fafc', verticalAlign: 'middle' },
  };

  return (
    <>
      <style>{CSS_PRINT}</style>
      <div style={s.page}>

        {/* ════ ENCABEZADO — flex 3 columnas ════ */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          borderBottom: '2px solid #1e293b', paddingBottom: 14, marginBottom: 14,
        }}>
          {/* Columna izquierda — vacía (simetría) */}
          <div style={{ flex: 1 }} />

          {/* Columna centro — nombre empresa + título */}
          <div style={{ flex: 2, textAlign: 'center' }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#1e293b' }}>
              {empresa?.nombre || empresa?.razon_social || 'FERRETERÍA'}
            </div>
            {empresa?.ruc && (
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>RUC: {empresa.ruc}</div>
            )}
            {empresa?.direccion && (
              <div style={{ fontSize: 10.5, color: '#64748b', marginTop: 2 }}>{empresa.direccion}</div>
            )}
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', marginTop: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
              Cuadre de Caja
            </div>
          </div>

          {/* Columna derecha — estado ABIERTA / CERRADA */}
          <div style={{ flex: 1, textAlign: 'right', paddingTop: 2 }}>
            <span style={{
              display: 'inline-block', padding: '4px 12px', borderRadius: 20,
              fontWeight: 800, fontSize: 11.5,
              background: estaAbierta ? '#dcfce7' : '#fee2e2',
              color:      estaAbierta ? '#166534' : '#991b1b',
              border:     `1px solid ${estaAbierta ? '#86efac' : '#fca5a5'}`,
              whiteSpace: 'nowrap',
            }}>
              {estaAbierta ? '● ABIERTA' : '● CERRADA'}
            </span>
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 5 }}>
              {fmtFecha(sesion.fecha)}
            </div>
          </div>
        </div>

        {/* ── Datos de sesión ── */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 20px', fontSize: 11.5, color: '#475569', marginBottom: 14 }}>
          {[
            ['Caja',     sesion.caja_nombre],
            sesion.almacen_nombre ? ['Almacén', sesion.almacen_nombre] : null,
            ['Apertura', fmtHora(sesion.abierto_en)],
            ['Cierre',   sesion.cerrado_en ? fmtHora(sesion.cerrado_en) : '—'],
          ].filter(Boolean).map(([lbl, val]) => (
            <span key={lbl} style={{ whiteSpace: 'nowrap' }}>
              <span style={{ color: '#94a3b8' }}>{lbl}: </span>
              <strong style={{ color: '#1e293b' }}>{val}</strong>
            </span>
          ))}
        </div>

        {/* ════ VENTAS ════ */}
        <div style={s.sec}>
          <div style={s.sTitle}>Ventas de la sesión</div>

          {(ventas_por_forma_pago || []).length > 0 && (
            <>
              <div style={{ ...s.row, background: '#f8fafc', fontWeight: 700 }}>
                <span>Ventas al Contado</span>
                <span style={{ color: '#2563eb', whiteSpace: 'nowrap' }}>S/ {fmt(totalContado)}</span>
              </div>
              {ventas_por_forma_pago.map(fp => (
                <div key={fp.abreviatura} style={s.rowSub}>
                  <span>{fp.forma_pago} ({fp.nro} vta{fp.nro !== 1 ? 's' : ''})</span>
                  <span style={{ whiteSpace: 'nowrap' }}>S/ {fmt(fp.monto)}</span>
                </div>
              ))}
            </>
          )}

          <div style={s.row}>
            <span style={s.lbl}>
              Ventas a Crédito / Mixto
              <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 6 }}>(saldo en CxC)</span>
            </span>
            <span style={{ ...s.val, color: '#7c3aed' }}>S/ {fmt(totalCredito)}</span>
          </div>

          <div style={s.rowTot}>
            <span>
              Total Ventas
              <span style={{ fontSize: 10.5, fontWeight: 400, color: '#64748b', marginLeft: 8 }}>
                {ventas?.nro_ventas || 0} vta(s)
                {(ventas?.nro_anuladas || 0) > 0 && ` / ${ventas.nro_anuladas} anulada(s)`}
              </span>
            </span>
            <span style={{ color: '#1d4ed8', whiteSpace: 'nowrap' }}>S/ {fmt(totalVentas)}</span>
          </div>
        </div>

        {/* ════ MOVIMIENTOS DE CAJA ════ */}
        {(movimientos || []).length > 0 && (
          <div style={s.sec}>
            <div style={s.sTitle}>Movimientos de caja</div>
            <table style={s.tbl}>
              <thead>
                <tr>
                  <th style={{ ...s.th, whiteSpace: 'nowrap' }}>Hora</th>
                  <th style={s.th}>Tipo</th>
                  <th style={s.th}>Concepto</th>
                  <th style={s.th}>Forma Pago</th>
                  <th style={{ ...s.th, textAlign: 'right', whiteSpace: 'nowrap' }}>Monto</th>
                </tr>
              </thead>
              <tbody>
                {movimientos.map(m => (
                  <tr key={m.id}>
                    <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{fmtHora(m.creado_en)}</td>
                    <td style={s.td}>
                      <span style={{
                        padding: '1px 6px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                        background: m.tipo === 'INGRESO' ? '#dcfce7' : '#fee2e2',
                        color:      m.tipo === 'INGRESO' ? '#166534' : '#991b1b',
                        whiteSpace: 'nowrap',
                      }}>{m.tipo}</span>
                    </td>
                    <td style={s.td}>{m.concepto || '—'}</td>
                    <td style={{ ...s.td, color: '#64748b', whiteSpace: 'nowrap' }}>
                      {m.forma_pago || (m.es_efectivo ? 'Efectivo' : '—')}
                    </td>
                    <td style={{
                      ...s.td, textAlign: 'right', fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                      color: m.tipo === 'INGRESO' ? '#16a34a' : '#dc2626',
                    }}>
                      {m.tipo === 'INGRESO' ? '+' : '-'}&nbsp;S/&nbsp;{fmt(m.monto)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ════ CUADRE DE EFECTIVO ════ */}
        <div style={s.sec}>
          <div style={s.sTitle}>Cuadre de efectivo</div>

          <div style={s.row}>
            <span style={s.lbl}>+ Monto apertura</span>
            <span style={s.val}>S/&nbsp;{fmt(monto_apertura)}</span>
          </div>
          <div style={s.row}>
            <span style={s.lbl}>+ Ventas efectivo (contado)</span>
            <span style={{ ...s.val, color: '#16a34a' }}>S/&nbsp;{fmt(ventas_efectivo)}</span>
          </div>
          <div style={s.row}>
            <span style={s.lbl}>
              + Ingresos efectivo
              <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 6 }}>(manuales + cobros + adelantos)</span>
            </span>
            <span style={{ ...s.val, color: '#16a34a' }}>S/&nbsp;{fmt(ingresos)}</span>
          </div>
          {parseFloat(ingresos_digitales || 0) > 0 && (
            <div style={{ ...s.row, opacity: 0.65 }}>
              <span style={{ color: '#2563eb' }}>
                Ingresos digitales
                <span style={{ fontSize: 10, marginLeft: 6 }}>(no suman al cajón)</span>
              </span>
              <span style={{ fontWeight: 600, color: '#2563eb', whiteSpace: 'nowrap' }}>
                S/&nbsp;{fmt(ingresos_digitales)}
              </span>
            </div>
          )}
          <div style={s.row}>
            <span style={s.lbl}>- Egresos</span>
            <span style={{ ...s.val, color: '#dc2626' }}>S/&nbsp;{fmt(egresos)}</span>
          </div>

          <div style={s.rowFin}>
            <span>TOTAL ESPERADO EN CAJA</span>
            <span style={{ whiteSpace: 'nowrap' }}>S/&nbsp;{fmt(total_efectivo)}</span>
          </div>

          {sesion.monto_cierre != null && (
            <>
              <div style={s.row}>
                <span style={s.lbl}>Monto declarado al cierre</span>
                <span style={s.val}>S/&nbsp;{fmt(sesion.monto_cierre)}</span>
              </div>
              {diferencia !== null && (
                <div style={{
                  ...s.rowTot,
                  color: diferencia > 0 ? '#16a34a' : diferencia < 0 ? '#dc2626' : '#64748b',
                }}>
                  <span>Diferencia</span>
                  <span style={{ whiteSpace: 'nowrap' }}>
                    {diferencia >= 0 ? '+' : ''}S/&nbsp;{fmt(diferencia)}
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Observaciones ── */}
        {(sesion.observacion_apertura || sesion.observacion_cierre) && (
          <div style={{ ...s.sec, fontSize: 11.5, color: '#64748b' }}>
            <div style={s.sTitle}>Observaciones</div>
            {sesion.observacion_apertura && (
              <div style={{ padding: '6px 10px', borderBottom: '1px solid #f1f5f9' }}>
                <strong>Apertura:</strong> {sesion.observacion_apertura}
              </div>
            )}
            {sesion.observacion_cierre && (
              <div style={{ padding: '6px 10px' }}>
                <strong>Cierre:</strong> {sesion.observacion_cierre}
              </div>
            )}
          </div>
        )}

        {/* ── Firmas ── */}
        <div style={{ marginTop: 40, display: 'flex', justifyContent: 'space-around' }}>
          {['Cajero(a)', 'Supervisor(a)'].map(rol => (
            <div key={rol} style={{ textAlign: 'center', minWidth: 160 }}>
              <div style={{ borderTop: '1px solid #1e293b', paddingTop: 4, fontSize: 11, color: '#64748b' }}>
                {rol}
              </div>
            </div>
          ))}
        </div>

      </div>
    </>
  );
}
